import { z } from 'zod';
import { DateOnly, HexColor, Id, IsoDate, PageQuerySchema, SearchQuery, UserRefSchema } from './common';
import { CustomFieldType, Priority, ViewType } from './enums';
/* ============================================================================
   Work: workspaces → folders → projects → missions (code: tasks) → objectives
   (PLAN §8). UI wording follows terms.mission / terms.neutral (terms.ts).
   ========================================================================== */
/* ---------- Workspaces, folders, clients ---------- */
export const WorkspaceSchema = z.object({
    id: Id,
    name: z.string(),
    slug: z.string(),
    sortOrder: z.number().int(),
    memberCount: z.number().int().nonnegative(),
    projectCount: z.number().int().nonnegative(),
    createdAt: IsoDate,
});
export const WorkspaceCreateSchema = z.object({ name: z.string().trim().min(2).max(60) });
export const WorkspaceUpdateSchema = z.object({
    name: z.string().trim().min(2).max(60).optional(),
    sortOrder: z.number().int().optional(),
});
export const WorkspaceDeleteSchema = z.object({ confirmName: z.string() });
export const FolderSchema = z.object({ id: Id, workspaceId: Id, name: z.string(), sortOrder: z.number().int() });
export const FolderCreateSchema = z.object({ workspaceId: Id, name: z.string().trim().min(1).max(60) });
export const FolderUpdateSchema = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    sortOrder: z.number().int().optional(),
});
export const ClientSchema = z.object({ id: Id, name: z.string(), projectCount: z.number().int().nonnegative() });
export const ClientUpsertSchema = z.object({ name: z.string().trim().min(1).max(80) });
/* ---------- Statuses ---------- */
export const StatusSchema = z.object({
    id: Id,
    projectId: Id,
    name: z.string(),
    color: HexColor,
    sortOrder: z.number().int(),
    /** closed statuses = "Accomplished" group */
    isClosed: z.boolean(),
});
export const StatusCreateSchema = z.object({
    name: z.string().trim().min(1).max(40),
    color: HexColor,
    isClosed: z.boolean().default(false),
});
export const StatusUpdateSchema = z.object({
    name: z.string().trim().min(1).max(40).optional(),
    color: HexColor.optional(),
    isClosed: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
/** Deleting a status moves its missions to another one. */
export const StatusDeleteSchema = z.object({ moveToStatusId: Id });
/* ---------- Projects ---------- */
export const ProjectSchema = z.object({
    id: Id,
    workspaceId: Id,
    folderId: Id.nullable(),
    client: ClientSchema.pick({ id: true, name: true }).nullable(),
    code: z.string(), // P-014
    keyPrefix: z.string(), // ACM
    name: z.string(),
    color: HexColor,
    description: z.string().nullable(),
    startAt: DateOnly.nullable(),
    dueAt: DateOnly.nullable(),
    archived: z.boolean(),
    progress: z.number().min(0).max(100),
    counts: z.object({
        total: z.number().int(),
        open: z.number().int(),
        overdue: z.number().int(),
        done: z.number().int(),
    }),
    members: z.array(UserRefSchema),
    statuses: z.array(StatusSchema),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const ProjectListQuery = SearchQuery.extend({
    workspaceId: Id.optional(),
    folderId: Id.optional(),
    clientId: Id.optional(),
    archived: z.coerce.boolean().optional(),
});
export const ProjectCreateSchema = z.object({
    workspaceId: Id,
    folderId: Id.nullable().optional(),
    clientId: Id.nullable().optional(),
    name: z.string().trim().min(2).max(80),
    keyPrefix: z
        .string()
        .regex(/^[A-Z]{2,5}$/)
        .optional(),
    color: HexColor.optional(),
    description: z.string().max(5000).optional(),
    startAt: DateOnly.optional(),
    dueAt: DateOnly.optional(),
    memberIds: z.array(Id).optional(),
});
export const ProjectUpdateSchema = ProjectCreateSchema.omit({ workspaceId: true })
    .partial()
    .extend({ archived: z.boolean().optional() });
export const ProjectDeleteSchema = z.object({ confirmName: z.string() });
export const ProjectMembersSchema = z.object({ userIds: z.array(Id) });
/* ---------- Custom fields ---------- */
export const CustomFieldOptionSchema = z.object({
    id: Id,
    label: z.string(),
    color: HexColor,
    sortOrder: z.number().int(),
});
export const CustomFieldSchema = z.object({
    id: Id,
    workspaceId: Id,
    /** null = all projects in the workspace */
    projectId: Id.nullable(),
    name: z.string(),
    type: z.enum(CustomFieldType),
    options: z.array(CustomFieldOptionSchema),
    config: z
        .object({
        currency: z.string().optional(),
        precision: z.number().int().optional(),
        max: z.number().int().optional(),
    })
        .default({}),
    sortOrder: z.number().int(),
    usedInProjects: z.number().int().nonnegative(),
    createdAt: IsoDate,
});
export const CustomFieldCreateSchema = z.object({
    workspaceId: Id,
    projectId: Id.nullable(),
    name: z.string().trim().min(1).max(40),
    type: z.enum(CustomFieldType),
    options: z.array(z.object({ label: z.string().min(1).max(40), color: HexColor })).optional(),
    config: z
        .object({
        currency: z.string().optional(),
        precision: z.number().int().optional(),
        max: z.number().int().optional(),
    })
        .optional(),
});
export const CustomFieldUpdateSchema = CustomFieldCreateSchema.omit({ workspaceId: true, type: true })
    .partial()
    .extend({ sortOrder: z.number().int().optional() });
export const CustomFieldDeleteSchema = z.object({ confirmName: z.string() });
/** A custom value: string | number | boolean | string[] (option ids / user ids) | null. */
export const CustomValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]);
/* ---------- Tags ---------- */
export const TagSchema = z.object({ id: Id, workspaceId: Id, name: z.string(), color: HexColor });
export const TagCreateSchema = z.object({ workspaceId: Id, name: z.string().trim().min(1).max(30), color: HexColor });
/* ---------- Missions (tasks) ---------- */
export const MissionSummarySchema = z.object({
    id: Id,
    /** human id, e.g. ACM-142 */
    key: z.string(),
    projectId: Id,
    parentId: Id.nullable(),
    title: z.string(),
    statusId: Id,
    priority: z.enum(Priority),
    startAt: DateOnly.nullable(),
    dueAt: DateOnly.nullable(),
    etaMinutes: z.number().int().nonnegative().nullable(),
    sortKey: z.string(),
    completedAt: IsoDate.nullable(),
    assignees: z.array(UserRefSchema),
    tags: z.array(TagSchema.pick({ id: true, name: true, color: true })),
    counts: z.object({
        objectives: z.number().int(),
        objectivesDone: z.number().int(),
        checklist: z.number().int(),
        checklistDone: z.number().int(),
        comments: z.number().int(),
        attachments: z.number().int(),
    }),
    customValues: z.record(Id, CustomValue),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const ChecklistItemSchema = z.object({
    id: Id,
    text: z.string(),
    done: z.boolean(),
    sortOrder: z.number().int(),
});
export const AttachmentSchema = z.object({
    id: Id,
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** short-lived signed URL (5 min), refetch the mission to renew */
    url: z.string().url(),
    thumbnailUrl: z.string().url().nullable(),
    uploadedBy: UserRefSchema,
    createdAt: IsoDate,
});
export const MissionDetailSchema = MissionSummarySchema.extend({
    description: z.string().nullable(),
    project: z.object({ id: Id, name: z.string(), code: z.string(), color: HexColor }),
    status: StatusSchema,
    objectives: z.array(MissionSummarySchema),
    checklist: z.array(ChecklistItemSchema),
    attachments: z.array(AttachmentSchema),
    watchers: z.array(UserRefSchema),
    watching: z.boolean(),
    createdBy: UserRefSchema,
    /** previous / next mission ids in the current list (J/K navigation) */
    neighbours: z.object({ prevId: Id.nullable(), nextId: Id.nullable() }),
});
/* ---------- Filters (rule builder) ---------- */
export const FilterRuleSchema = z.object({
    field: z.union([
        z.enum(['status', 'assignee', 'priority', 'dueAt', 'startAt', 'tag', 'createdBy', 'title']),
        z.string().regex(/^cf:[0-9a-f-]{36}$/), // custom field
    ]),
    op: z.enum([
        'is',
        'is_not',
        'in',
        'not_in',
        'before',
        'after',
        'between',
        'is_empty',
        'is_not_empty',
        'contains',
        'gt',
        'lt',
    ]),
    value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]).optional(),
});
export const SavedFilterSchema = z.object({
    match: z.enum(['all', 'any']).default('all'),
    rules: z.array(FilterRuleSchema).max(20),
    quick: z
        .object({
        me: z.boolean().optional(),
        showClosed: z.boolean().optional(),
        due: z.enum(['overdue', 'today', 'this_week', 'next_week', 'no_date']).optional(),
    })
        .default({}),
});
export const MissionSort = z.enum([
    'manual',
    'dueAt',
    '-dueAt',
    'priority',
    'createdAt',
    '-createdAt',
    'title',
    'updatedAt',
]);
/** List missions. Grouped views paginate per group: pass groupValue + its own cursor. */
export const MissionListQuery = PageQuerySchema.merge(SearchQuery).extend({
    projectId: Id.optional(),
    groupBy: z.enum(['status', 'assignee', 'priority', 'dueAt', 'none']).default('status'),
    groupValue: z.string().optional(),
    sort: MissionSort.default('manual'),
    /** JSON-encoded SavedFilter */
    filter: z.string().max(4000).optional(),
    parentId: Id.optional(),
    /** calendar range */
    from: DateOnly.optional(),
    to: DateOnly.optional(),
});
/** First screen of a grouped list: groups with counts + first page each. */
export const MissionGroupSchema = z.object({
    value: z.string(),
    label: z.string(),
    color: HexColor.nullable(),
    count: z.number().int().nonnegative(),
    isClosed: z.boolean(),
    items: z.array(MissionSummarySchema),
    nextCursor: z.string().nullable(),
});
export const MissionGroupsSchema = z.object({ groups: z.array(MissionGroupSchema) });
export const MissionCreateSchema = z.object({
    projectId: Id,
    parentId: Id.nullable().optional(),
    title: z.string().trim().min(1).max(300),
    description: z.string().max(20000).optional(),
    statusId: Id.optional(),
    priority: z.enum(Priority).optional(),
    startAt: DateOnly.nullable().optional(),
    dueAt: DateOnly.nullable().optional(),
    etaMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    assigneeIds: z.array(Id).optional(),
    tagIds: z.array(Id).optional(),
    customValues: z.record(Id, CustomValue).optional(),
    /** insert position for drag & drop / inline add */
    afterId: Id.nullable().optional(),
});
export const MissionUpdateSchema = MissionCreateSchema.omit({ projectId: true, parentId: true }).partial();
/** Move within/between groups or projects (board drag & drop). */
export const MissionMoveSchema = z.object({
    statusId: Id.optional(),
    projectId: Id.optional(),
    beforeId: Id.nullable().optional(),
    afterId: Id.nullable().optional(),
});
export const MissionBulkSchema = z.object({
    ids: z.array(Id).min(1).max(500),
    action: z.discriminatedUnion('type', [
        z.object({ type: z.literal('status'), statusId: Id }),
        z.object({ type: z.literal('assign'), assigneeIds: z.array(Id), mode: z.enum(['add', 'replace', 'remove']) }),
        z.object({ type: z.literal('due'), dueAt: DateOnly.nullable() }),
        z.object({ type: z.literal('priority'), priority: z.enum(Priority) }),
        z.object({ type: z.literal('move'), projectId: Id, statusId: Id.optional() }),
        z.object({ type: z.literal('delete') }),
        z.object({ type: z.literal('restore') }),
    ]),
});
export const MissionBulkResultSchema = z.object({
    updated: z.number().int(),
    failed: z.array(z.object({ id: Id, code: z.string() })),
    /** token for the Undo toast (valid 30s) */
    undoToken: z.string().nullable(),
});
export const UndoSchema = z.object({ undoToken: z.string() });
export const ChecklistCreateSchema = z.object({ text: z.string().trim().min(1).max(300) });
export const ChecklistUpdateSchema = z.object({
    text: z.string().trim().min(1).max(300).optional(),
    done: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const AttachmentCreateSchema = z.object({ uploadId: Id, fileName: z.string().max(255) });
/* ---------- Comments & reactions ---------- */
export const CommentSchema = z.object({
    id: Id,
    missionId: Id,
    author: UserRefSchema,
    body: z.string(),
    mentions: z.array(UserRefSchema),
    attachments: z.array(AttachmentSchema),
    reactions: z.array(z.object({ emoji: z.string(), count: z.number().int(), mine: z.boolean() })),
    editedAt: IsoDate.nullable(),
    createdAt: IsoDate,
});
export const CommentCreateSchema = z.object({
    body: z.string().trim().min(1).max(10000),
    mentionIds: z.array(Id).optional(),
    attachmentUploadIds: z.array(Id).optional(),
});
export const CommentUpdateSchema = z.object({ body: z.string().trim().min(1).max(10000) });
export const ReactionSchema = z.object({ emoji: z.string().min(1).max(16) });
/* ---------- Views (saved filters / layouts) ---------- */
export const ViewSchema = z.object({
    id: Id,
    workspaceId: Id,
    projectId: Id.nullable(),
    name: z.string(),
    type: z.enum(ViewType),
    isShared: z.boolean(),
    mine: z.boolean(),
    config: z.object({
        filter: SavedFilterSchema,
        groupBy: z.enum(['status', 'assignee', 'priority', 'dueAt', 'none']),
        sort: MissionSort,
        columns: z.array(z.string()),
        fullScreen: z.boolean().optional(),
    }),
    createdAt: IsoDate,
});
export const ViewCreateSchema = ViewSchema.pick({ name: true, type: true, isShared: true, config: true }).extend({
    projectId: Id.nullable(),
    workspaceId: Id,
});
export const ViewUpdateSchema = ViewSchema.pick({ name: true, isShared: true, config: true }).partial();
/* ---------- Search, My Missions, Mission Control ---------- */
export const SearchResultSchema = z.object({
    missions: z.array(MissionSummarySchema.pick({ id: true, key: true, title: true, projectId: true, statusId: true, dueAt: true })),
    projects: z.array(ProjectSchema.pick({ id: true, name: true, code: true, color: true })),
    people: z.array(UserRefSchema),
});
export const MyMissionsQuery = PageQuerySchema.extend({
    bucket: z.enum(['overdue', 'today', 'this_week', 'later', 'no_date']),
    includeWatched: z.coerce.boolean().optional(),
});
export const MyMissionsCountsSchema = z.object({
    overdue: z.number().int(),
    today: z.number().int(),
    this_week: z.number().int(),
    later: z.number().int(),
    no_date: z.number().int(),
});
export const MissionControlSchema = z.object({
    briefing: z.object({
        date: DateOnly,
        dueToday: z.number().int(),
        overdue: z.number().int(),
        mentions: z.number().int(),
        plannedMinutes: z.number().int(),
    }),
    stats: z.object({
        activeProjects: z.number().int(),
        completedThisMonth: z.number().int(),
        overdue: z.number().int(),
        dueThisWeek: z.number().int(),
    }),
    activeProjects: z.array(ProjectSchema.pick({
        id: true,
        name: true,
        code: true,
        color: true,
        client: true,
        dueAt: true,
        progress: true,
        counts: true,
        members: true,
    })),
    upcoming: z.array(MissionSummarySchema),
    workload: z.array(z.object({
        user: UserRefSchema,
        open: z.number().int(),
        overdue: z.number().int(),
        plannedMinutes: z.number().int(),
    })),
    completedSeries: z.array(z.object({ date: DateOnly, count: z.number().int() })),
});
/* ---------- Activity timeline (ClickUp-style column) ---------- */
export const TimelineItemSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('comment'), id: Id, at: IsoDate, comment: CommentSchema }),
    z.object({
        kind: z.literal('activity'),
        id: Id,
        at: IsoDate,
        actor: UserRefSchema.nullable(),
        action: z.string(), // e.g. "status.changed"
        data: z.record(z.unknown()),
        /** consecutive changes by the same actor within 10 min, collapsed */
        group: z.array(z.object({ action: z.string(), data: z.record(z.unknown()), at: IsoDate })).optional(),
    }),
]);
export const TimelineQuery = PageQuerySchema.extend({
    filter: z.enum(['all', 'comments', 'activity', 'mentions']).default('all'),
    q: z.string().max(200).optional(),
});
//# sourceMappingURL=work.js.map